import { useQuery } from '@tanstack/react-query';
import dayjs from 'dayjs';
import isEmpty from 'lodash/isEmpty';
import React, { useContext, useEffect, useMemo } from 'react';
import { useFormContext } from 'react-hook-form';

import { FormBlock, StepProps } from '@/widgets/ServiceFormTemplate';

import { DocumentsData } from '@/features/ui/DocumentsData';

import { RMPServiceCode } from '@/shared/types/serviceCodes';
import {
  FormTextInput,
  StepControlPanel,
  ConditionView,
  FormSelect,
  FormDatePicker,
  FormSwitch,
  UKRAINE_PHONE_MASK_START,
  Grid,
} from '@/shared/ui';
import { AddressBlock, addressBlockPreview } from '@/shared/ui/AddressBlock';
import { AddressBlockSchema } from '@/shared/ui/AddressBlock/model';
import { MultipleFormInputPhone } from '@/shared/ui/InputPhone/MultipleFormInputPhone';

import { FormConstants, IdConstants } from '@/components/Forms/constants';

import { useERZDocumentTypes } from '@/modules/erz/helpers';

import { formatDate } from '@/helpers/dates';

import AuthContext from '@/contexts/Auth/AuthContext';

import { LEGAL_AGE } from '@/lib/consts';

import { rmp } from '@/api/integrations/rmp';
import { queryKeys } from '@/api/queryKeys';
import { DocumentFieldName, DocumentTypeCode } from '@/api/types';

import api from '@/api';

import { missingPersonExtractAllowedFileTypes } from '../../../MissingPersonExtract/lib/constants';
import { OTHER_ANCESTRAL_CONNECTION_ID, YesNoSelectOptions } from '../../lib/constants';
import { MissingPersonFormData } from '../../model/types';

export const ApplicantPersonalData: React.FC<StepProps> = ({
  next,
  prev,
  serviceCode,
  currentRequestId,
  onStepDraftSave,
}) => {
  const { control, formState, getValues, watch, setValue } = useFormContext<MissingPersonFormData>();
  const {
    userData,
    addressesData: { BIRTH, REGISTRATION, LIVING },
    address_equal,
  } = useContext(AuthContext);

  const { applicantRegisteredAddress, applicantResidenceAddress } = watch();

  const [isAncestralConnectionChanged, setIsAncestralConnectionChanged] = React.useState<boolean>(false);
  const phones = getValues('applicantData.phones');
  const withoutCitizenship = watch('applicantData.without_citizenship');
  const ancestralConnection = watch('applicantData.ancestral_connection');
  const ancestralConnectionDocument = watch('applicantData.documents_proofs_of_ancestral_connection')?.[0];

  const { data: optionsCountry = [], isLoading: isLoadingCountry } = useQuery({
    queryKey: queryKeys.dictionary.countries(),
    queryFn: () => api.dictionary.getCountry().then((r) => r.data.map((c) => ({ id: c.id, label: c.shortName }))),
  });

  const { data: ancestralOptions = [] } = useQuery({
    queryKey: queryKeys.rmp.familyRelationshipDegrees(serviceCode as string),
    queryFn: () =>
      rmp.dictionary.getFamilyRelationshipDegrees(serviceCode as RMPServiceCode.MISSING_PERSON).then((r) =>
        r.data.map((d) => ({
          id: d.id,
          label: d.name,
          required_documents: d.required_documents.map((rd) => ({ id: rd.id, label: rd.name })),
        })),
      ),
    enabled: !!serviceCode,
  });

  const { data: ancestralConnectionDocs = [] } = useERZDocumentTypes({
    allowedDocumentTypeCodes: [DocumentTypeCode.ANCESTRAL_CONNECTION],
  });
  const ancestralConnectionDocType = ancestralConnectionDocs[0];

  useEffect(() => {
    const formValues = getValues();
    if (!userData) return;

    isEmpty(formValues.applicantData?.first_name) && setValue('applicantData.first_name', userData?.name_first ?? '');
    isEmpty(formValues.applicantData?.last_name) && setValue('applicantData.last_name', userData?.name_last ?? '');
    isEmpty(formValues.applicantData?.patronymic) &&
      userData?.name_middle &&
      setValue('applicantData.patronymic', userData?.name_middle);
    isEmpty(formValues.applicantData?.birthday) &&
      userData?.date_of_birth &&
      setValue('applicantData.birthday', userData?.date_of_birth);
    isEmpty(formValues.applicantData?.birth_place) && setValue('applicantData.birth_place', BIRTH.country ?? '');
    isEmpty(formValues.applicantData?.phones) && setValue('applicantData.phones', [userData?.contact_phone ?? '']);
    isEmpty(formValues.applicantData?.rnokpp) && setValue('applicantData.rnokpp', userData?.drfo_code ?? '');

    isEmpty(formValues.applicantData?.without_citizenship) &&
      setValue(
        'applicantData.without_citizenship',
        !userData?.citizenship || userData?.citizenship?.id ? YesNoSelectOptions.NO : YesNoSelectOptions.YES,
      );
  }, [userData]);

  useEffect(() => {
    if (isLoadingCountry) return;
    const formValues = getValues();

    const ukraine = optionsCountry.find(({ id }) => id === IdConstants.UKRAINE);
    if (isEmpty(formValues.applicantData?.citizenship) && !Number(withoutCitizenship?.id)) {
      setValue('applicantData.citizenship', userData?.citizenship ? userData?.citizenship : ukraine);
    }
  }, [isLoadingCountry]);

  useEffect(() => {
    if (!isAncestralConnectionChanged) return;

    const ids = Array.isArray(ancestralConnectionDocument?.attachments?.files)
      ? ancestralConnectionDocument?.attachments?.files.filter(Boolean)
      : [];
    ids.length > 0 && api.services.deleteAttachments({ request_id: currentRequestId ?? '', ids });

    setValue(`applicantData.documents_proofs_of_ancestral_connection`, [
      {
        info: {},
        attachments: [],
      },
    ]);
    setValue(
      `applicantData.documents_proofs_of_ancestral_connection.0.${DocumentFieldName.type}`,
      // @ts-ignore
      ancestralConnectionDocType,
    );
  }, [ancestralConnection, isAncestralConnectionChanged]);

  useEffect(() => {
    const formValues = getValues();

    const addrRegister = !isEmpty(formValues.applicantRegisteredAddress)
      ? formValues.applicantRegisteredAddress
      : {
          country: isEmpty(formValues.applicantResidenceAddress?.country)
            ? (REGISTRATION.country ?? { id: IdConstants.UKRAINE, label: 'Україна' })
            : formValues.applicantResidenceAddress?.country,
          region: isEmpty(formValues.applicantResidenceAddress?.region)
            ? REGISTRATION.region
            : formValues.applicantResidenceAddress?.region,
          district: isEmpty(formValues.applicantResidenceAddress?.district)
            ? REGISTRATION.district
            : formValues.applicantResidenceAddress?.district,
          settlement: isEmpty(formValues.applicantResidenceAddress?.settlement)
            ? REGISTRATION.settlement
            : formValues.applicantResidenceAddress?.settlement,
          street: isEmpty(formValues.applicantResidenceAddress?.street)
            ? REGISTRATION.street
            : formValues.applicantResidenceAddress?.street,
          house: isEmpty(formValues.applicantResidenceAddress?.house)
            ? REGISTRATION.house
            : formValues.applicantResidenceAddress?.house,
          building: isEmpty(formValues.applicantResidenceAddress?.building)
            ? REGISTRATION.building
            : formValues.applicantResidenceAddress?.building,
          apartment: isEmpty(formValues.applicantResidenceAddress?.apartment)
            ? REGISTRATION.apartment
            : formValues.applicantResidenceAddress?.apartment,
          zip_code: null,
        };

    const addrLiving = !isEmpty(formValues.applicantResidenceAddress)
      ? formValues.applicantResidenceAddress
      : {
          country: isEmpty(formValues.applicantResidenceAddress?.country)
            ? (LIVING.country ?? { id: IdConstants.UKRAINE, label: 'Україна' })
            : formValues.applicantResidenceAddress?.country,
          region: isEmpty(formValues.applicantResidenceAddress?.region)
            ? LIVING.region
            : formValues.applicantResidenceAddress?.region,
          district: isEmpty(formValues.applicantResidenceAddress?.district)
            ? LIVING.district
            : formValues.applicantResidenceAddress?.district,
          settlement: isEmpty(formValues.applicantResidenceAddress?.settlement)
            ? LIVING.settlement
            : formValues.applicantResidenceAddress?.settlement,
          street: isEmpty(formValues.applicantResidenceAddress?.street)
            ? LIVING.street
            : formValues.applicantResidenceAddress?.street,
          house: isEmpty(formValues.applicantResidenceAddress?.house)
            ? LIVING.house
            : formValues.applicantResidenceAddress?.house,
          building: isEmpty(formValues.applicantResidenceAddress?.building)
            ? LIVING.building
            : formValues.applicantResidenceAddress?.building,
          apartment: isEmpty(formValues.applicantResidenceAddress?.apartment)
            ? LIVING.apartment
            : formValues.applicantResidenceAddress?.apartment,
          zip_code: null,
          address_equal: isEmpty(formValues.applicantResidenceAddress?.address_equal)
            ? address_equal
            : (formValues.applicantResidenceAddress?.address_equal ?? false),
        };

    // @ts-ignore
    setValue('applicantRegisteredAddress', addrRegister);
    // @ts-ignore
    setValue('applicantResidenceAddress', addrLiving);
  }, [REGISTRATION, LIVING]);

  const regAddressBlockValue = useMemo<AddressBlockSchema>(() => {
    const addr = applicantRegisteredAddress || {};
    return {
      country: addr.country ?? null,
      apartment: addr.apartment || null,
      building: addr.building || null,
      district: addr.district || null,
      house: addr.house || null,
      region: addr.region || null,
      settlement: addr.settlement || null,
      street: addr.street || null,
      custom_street: addr.custom_street || null,
      zip_code: addr.zip_code || null,
      // street_custom: addr.street_custom || null,
      // settlement_custom: addr.settlement_custom || null,
    };
  }, [applicantRegisteredAddress]);

  const resAddressBlockValue = useMemo<AddressBlockSchema>(() => {
    const addr = applicantResidenceAddress || {};
    return {
      country: addr.country ?? null,
      apartment: addr.apartment || null,
      building: addr.building || null,
      district: addr.district || null,
      house: addr.house || null,
      region: addr.region || null,
      settlement: addr.settlement || null,
      street: addr.street || null,
      custom_street: addr.custom_street || null,
      zip_code: addr.zip_code || null,
      // settlement_custom: addr.settlement_custom || null,
      // street_custom: addr.street_custom || null,
    };
  }, [applicantResidenceAddress]);

  const addressEqual = watch('applicantResidenceAddress.address_equal');

  const isWithoutCitizenship = withoutCitizenship?.id === YesNoSelectOptions.NO.id;

  return (
    <>
      <FormBlock title="Особисті дані заявника">
        <FormTextInput
          disabled
          name="applicantData.last_name"
          control={control}
          label="Прізвище"
          required
          maxLength={FormConstants.INPUT_MAX_LENGTH_255}
        />
        <FormTextInput
          disabled
          name="applicantData.first_name"
          control={control}
          label="Ім'я"
          required
          maxLength={FormConstants.INPUT_MAX_LENGTH_255}
        />
        <FormTextInput
          disabled
          name="applicantData.patronymic"
          control={control}
          label="По батькові"
          maxLength={FormConstants.INPUT_MAX_LENGTH_255}
        />
        <FormBlock title="Родинні звʼязки">
          <FormSelect
            name="applicantData.ancestral_connection"
            label="Ким ви приходитесь зниклій особі"
            control={control}
            options={ancestralOptions}
            onChange={() => setIsAncestralConnectionChanged(true)}
            required
          />

          <ConditionView if={ancestralConnection?.id === OTHER_ANCESTRAL_CONNECTION_ID}>
            <FormTextInput
              name="applicantData.other_ancestral_connection"
              control={control}
              label="Хто ви для зниклої особи"
              maxLength={FormConstants.INPUT_MAX_LENGTH_50}
              required
            />
          </ConditionView>
          {ancestralConnection && (
            <DocumentsData
              fileRequired={ancestralConnection.id !== OTHER_ANCESTRAL_CONNECTION_ID}
              withUploader
              documentTypeSelectHidden
              maxDocumentsCount={1}
              maxFileCount={3}
              name="applicantData.documents_proofs_of_ancestral_connection"
              documentUploadLabel="Документ, що підтверджує родинні зв’язки зі зниклою особою"
              request_id={currentRequestId ?? ''}
              documentTypeEntities={ancestralConnectionDocType ? [ancestralConnectionDocType] : []}
              onSaveDraft={onStepDraftSave}
              allowedFileTypes={missingPersonExtractAllowedFileTypes}
            />
          )}
        </FormBlock>
        <FormDatePicker
          name="applicantData.birthday"
          control={control}
          label="Дата народження"
          required
          maxDate={dayjs().subtract(LEGAL_AGE, 'years')}
        />
        <FormSelect
          name="applicantData.without_citizenship"
          control={control}
          label="Особа без громадянства"
          required
          options={[YesNoSelectOptions.YES, YesNoSelectOptions.NO]}
          onChange={(value: string) =>
            setValue('applicantData.citizenship', (Number(value) ? { id: null, label: null } : null) as any)
          }
        />
        {isWithoutCitizenship && (
          <FormSelect
            name="applicantData.citizenship"
            control={control}
            options={optionsCountry}
            label="Громадянство"
            required
          />
        )}
        <FormTextInput
          name="applicantData.birth_place"
          control={control}
          label="Місце народження"
          required
          maxLength={FormConstants.INPUT_MAX_LENGTH}
        />

        <MultipleFormInputPhone
          required
          onChange={(newPhones) => newPhones && setValue('applicantData.phones', newPhones)}
          value={phones}
          onlyCountries={[]}
          errors={formState.errors.applicantData?.phones as Array<{ message: string }>}
          maxPhoneNumbersQuantity={4}
        />

        <FormTextInput
          name="applicantData.rnokpp"
          maxLength={10}
          control={control}
          label="Ідентифікаційний код (РНОКПП)"
          disabled
        />
      </FormBlock>

      <FormBlock title="Адреса реєстрації">
        <AddressBlock
          withCustomStreet
          address={regAddressBlockValue}
          revalidate={!isEmpty(formState.errors)}
          includeZipCode={false}
          handleAddressChange={(newAddress): void => {
            setValue('applicantRegisteredAddress', {
              ...newAddress,
            } as MissingPersonFormData['applicantRegisteredAddress']);
          }}
        />
      </FormBlock>
      <FormBlock title="Вкажіть адресу проживання">
        <FormSwitch
          name="applicantResidenceAddress.address_equal"
          control={control}
          label="Адреса реєстрації співпадає з місцем проживання?"
        />
        {!addressEqual && (
          <AddressBlock
            withCustomStreet
            address={resAddressBlockValue}
            revalidate={!isEmpty(formState.errors)}
            includeZipCode={false}
            handleAddressChange={(newAddress): void => {
              setValue('applicantResidenceAddress', {
                ...newAddress,
                address_equal: addressEqual || false,
              } as MissingPersonFormData['applicantResidenceAddress']);
            }}
          />
        )}
      </FormBlock>

      <StepControlPanel prev={prev} next={next} />
    </>
  );
};

export const ApplicantPersonalDataPreview: React.FC = () => {
  const { getValues } = useFormContext<MissingPersonFormData>();
  const { applicantData, applicantRegisteredAddress, applicantResidenceAddress } = getValues();
  const addressEqual = applicantResidenceAddress?.address_equal;

  const items: Array<[string, Nullable<string>]> = [
    ['Прізвище', applicantData?.last_name],
    ['Ім’я', applicantData?.first_name],
    ['По батькові', applicantData?.patronymic],
    ['Ким ви приходитесь зниклій особі', applicantData?.ancestral_connection?.label],
    [
      'Документ, що підтверджує родинні зв’язки зі зниклою особою',
      applicantData?.documents_proofs_of_ancestral_connection?.[0]?.attachments?.fileNames
        ? Object.values(applicantData?.documents_proofs_of_ancestral_connection?.[0]?.attachments?.fileNames).join(', ')
        : (applicantData?.documents_proofs_of_ancestral_connection?.[0]?.attachments?.file_name ??
          applicantData?.documents_proofs_of_ancestral_connection?.[0]?.attachments?.fileName ??
          null),
    ] as [string, string],
    applicantData?.ancestral_connection?.id === OTHER_ANCESTRAL_CONNECTION_ID
      ? ['Хто ви для зниклої особи', applicantData?.other_ancestral_connection]
      : ['', ''],
    ['Дата народження', formatDate(applicantData?.birthday)],
    ['Місце народження', applicantData?.birth_place],
    ['Особа без громадянства', applicantData?.without_citizenship?.label],
    ['Громадянство', applicantData?.citizenship?.label],
    [
      'Номер телефона',
      applicantData?.phones
        ?.filter((phone) => phone !== UKRAINE_PHONE_MASK_START)
        ?.map((phone) => phone)
        ?.join(', '),
    ],
    ['Ідентифікаційний код (РНОКПП)', applicantData?.rnokpp],
  ];

  return (
    <>
      <Grid title="Особисті дані заявника" data={items} />

      <Grid
        title="Адреса реєстрації"
        data={addressBlockPreview({
          address: applicantRegisteredAddress,
          includeZipCode: false,
          enableCustomAddressFields: true,
        })}
      />

      <Grid
        title="Адреса проживання"
        data={[['Адреса реєстрації співпадає з місцем проживання', addressEqual ? 'Так' : 'Ні']]}
      />

      {!addressEqual && (
        <Grid
          title="Вкажіть адресу проживання"
          data={addressBlockPreview({
            address: applicantResidenceAddress,
            includeZipCode: false,
            enableCustomAddressFields: true,
          })}
        />
      )}
    </>
  );
};
